import React, { useState, useEffect, useMemo } from 'react';
import { MapPin, Calendar, RefreshCw, Save, Check, X, User, Navigation, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { DivIcon, LatLngExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import useAppStore from '../store';
import SearchableSelect from '../components/SearchableSelect';

interface TaskPosition {
  taskId: string;
  driverId: string;
  driverName: string;
  clientId: string;
  clientName: string;
  lat: number;
  lng: number;
  endTime: string;
  positionSource: 'gps_real' | 'drop_location';
  gpsDiffSeconds: number | null;
}

interface ClientLocation {
  id: string;
  name: string;
  position_lat: number;
  position_lng: number;
  is_primary: boolean;
}

interface SavePointState {
  taskId: string | null;
  name: string;
  isPrimary: boolean;
  saving: boolean;
  success: boolean;
  error: string | null;
}

const MONTHS = [
  { value: 0, label: 'Tous les mois' },
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Février' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' },
];

const defaultPosition: LatLngExpression = [46.603354, 1.888334];

const makeIcon = (color: string) => new DivIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41" width="25" height="41">
    <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 9.375 12.5 28.5 12.5 28.5S25 21.875 25 12.5C25 5.596 19.404 0 12.5 0z" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
    <circle cx="12.5" cy="12.5" r="5" fill="#ffffff"/>
  </svg>`,
  className: '',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

const gpsRealIcon = makeIcon('#16a34a');
const dropLocationIcon = makeIcon('#ea580c');
const clientLocIcon = makeIcon('#2563eb');

const PositionsPage: React.FC = () => {
  const { currentUser, clients } = useAppStore();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number>(0);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [positions, setPositions] = useState<TaskPosition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [clientLocations, setClientLocations] = useState<ClientLocation[]>([]);
  const [clientLocationsLoading, setClientLocationsLoading] = useState(false);

  const [savePoint, setSavePoint] = useState<SavePointState>({
    taskId: null,
    name: '',
    isPrimary: false,
    saving: false,
    success: false,
    error: null
  });

  const [drivers, setDrivers] = useState<{ id: string; name: string }[]>([]);

  const hasAccess = currentUser?.role === 'admin' || currentUser?.role === 'logistics';

  useEffect(() => {
    const fetchAvailableYears = async () => {
      try {
        const { data, error } = await supabase
          .from('tasks')
          .select('end_time')
          .eq('type', 'dumpster')
          .eq('status', 'completed')
          .not('end_time', 'is', null)
          .order('end_time', { ascending: false });

        if (error) throw error;

        if (data && data.length > 0) {
          const years = [...new Set(data.map(t => new Date(t.end_time).getFullYear()))];
          setAvailableYears(years.sort((a, b) => b - a));
        }
      } catch (err) {
        console.error('Error fetching available years:', err);
      }
    };

    const fetchDrivers = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('id, first_name, last_name')
          .eq('role', 'driver')
          .order('first_name');
        if (error) throw error;
        setDrivers((data || []).map(u => ({
          id: u.id,
          name: `${u.first_name} ${u.last_name}`
        })));
      } catch (err) {
        console.error('Error fetching drivers:', err);
      }
    };

    if (hasAccess) {
      fetchAvailableYears();
      fetchDrivers();
    }
  }, [hasAccess]);

  useEffect(() => {
    if (availableYears.length > 0 && selectedYear === null) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  useEffect(() => {
    const fetchPositions = async () => {
      if (!selectedYear) return;
      setLoading(true);
      setError(null);

      try {
        const { data, error: rpcError } = await supabase.rpc('get_task_gps_positions', {
          p_year: selectedYear,
          p_month: selectedMonth,
          p_driver_id: selectedDriverId || null
        });

        if (rpcError) throw rpcError;

        setPositions((data || []).map((row: any) => ({
          taskId: row.task_id,
          driverId: row.driver_id,
          driverName: row.driver_name,
          clientId: row.client_id,
          clientName: row.client_name,
          lat: row.lat,
          lng: row.lng,
          endTime: row.end_time,
          positionSource: row.position_source,
          gpsDiffSeconds: row.gps_diff_seconds
        })));
      } catch (err) {
        console.error('Error fetching positions:', err);
        setError('Erreur lors de la récupération des positions');
      } finally {
        setLoading(false);
      }
    };

    if (hasAccess) fetchPositions();
  }, [selectedYear, selectedMonth, selectedDriverId, hasAccess]);

  useEffect(() => {
    const fetchClientLocations = async () => {
      if (!selectedClientId) {
        setClientLocations([]);
        return;
      }
      setClientLocationsLoading(true);
      try {
        const { data, error } = await supabase
          .from('client_locations')
          .select('id, name, position_lat, position_lng, is_primary')
          .eq('client_id', selectedClientId)
          .order('is_primary', { ascending: false });
        if (error) throw error;
        setClientLocations(data || []);
      } catch (err) {
        console.error('Error fetching client locations:', err);
      } finally {
        setClientLocationsLoading(false);
      }
    };
    fetchClientLocations();
  }, [selectedClientId]);

  const clientOptions = useMemo(() =>
    [...clients]
      .sort((a, b) => a.companyName.localeCompare(b.companyName))
      .map(client => ({
        value: client.id,
        label: `${client.companyName} (${client.firstName} ${client.lastName})`
      })),
    [clients]
  );

  const driverOptions = useMemo(() =>
    drivers.map(d => ({ value: d.id, label: d.name })),
    [drivers]
  );

  const selectedClient = useMemo(() =>
    clients.find(c => c.id === selectedClientId) || null,
    [clients, selectedClientId]
  );

  const stats = useMemo(() => ({
    total: positions.length,
    gpsReal: positions.filter(p => p.positionSource === 'gps_real').length,
    dropLocation: positions.filter(p => p.positionSource === 'drop_location').length,
  }), [positions]);

  const mapCenter = useMemo(() => {
    const allPoints = [
      ...positions.map(p => ({ lat: p.lat, lng: p.lng })),
      ...clientLocations.map(l => ({ lat: l.position_lat, lng: l.position_lng }))
    ];
    if (allPoints.length === 0) return defaultPosition;
    const avgLat = allPoints.reduce((s, p) => s + p.lat, 0) / allPoints.length;
    const avgLng = allPoints.reduce((s, p) => s + p.lng, 0) / allPoints.length;
    return [avgLat, avgLng] as LatLngExpression;
  }, [positions, clientLocations]);

  const handleOpenSavePoint = (taskId: string, pos: TaskPosition) => {
    setSavePoint({
      taskId,
      name: `Point ${format(new Date(pos.endTime), 'dd/MM/yyyy', { locale: fr })} - ${pos.clientName.split(' (')[0]}`,
      isPrimary: false,
      saving: false,
      success: false,
      error: null
    });
  };

  const handleSavePoint = async (pos: TaskPosition) => {
    if (!savePoint.name.trim()) {
      setSavePoint(prev => ({ ...prev, error: 'Le nom du point est requis' }));
      return;
    }
    setSavePoint(prev => ({ ...prev, saving: true, error: null }));
    const { error: insertError } = await supabase
      .from('client_locations')
      .insert({
        client_id: pos.clientId,
        name: savePoint.name.trim(),
        position_lat: pos.lat,
        position_lng: pos.lng,
        is_primary: savePoint.isPrimary,
        created_by: currentUser?.id
      });

    if (insertError) {
      const msg = insertError.code === '23505'
        ? 'Un point avec ce nom existe déjà pour ce client'
        : "Erreur lors de l'enregistrement";
      setSavePoint(prev => ({ ...prev, saving: false, error: msg }));
      return;
    }
    setSavePoint(prev => ({ ...prev, saving: false, success: true }));
  };

  const handleCancelSave = (taskId: string) => {
    setSavePoint(prev => prev.taskId === taskId
      ? { taskId: null, name: '', isPrimary: false, saving: false, success: false, error: null }
      : prev
    );
  };

  if (!hasAccess) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2">Accès restreint</h2>
        <p className="text-gray-600">Cette page est réservée aux administrateurs et au personnel logistique.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <MapPin className="h-6 w-6" />
            Positions des Bennes
          </h1>
          <p className="text-gray-600 mt-1">
            Position GPS réelle du chauffeur au moment de la fin de chaque tâche benne
          </p>
        </div>

        {/* Filtres + Client + Légende + Stats sur une seule ligne */}
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              <Calendar className="inline h-3 w-3 mr-0.5" />Année
            </label>
            <select
              value={selectedYear || ''}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={availableYears.length === 0}
            >
              {availableYears.length === 0 && <option value="">...</option>}
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              <Calendar className="inline h-3 w-3 mr-0.5" />Mois
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {MONTHS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="w-44">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              <User className="inline h-3 w-3 mr-0.5" />Chauffeur
            </label>
            <div className="flex gap-1 h-9">
              <SearchableSelect
                options={driverOptions}
                value={selectedDriverId}
                onChange={setSelectedDriverId}
                placeholder="Tous"
                searchPlaceholder="Rechercher..."
                className="flex-1"
              />
              {selectedDriverId && (
                <button
                  onClick={() => setSelectedDriverId('')}
                  className="h-9 w-9 flex items-center justify-center border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          <div className="w-52">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              <User className="inline h-3 w-3 mr-0.5" />Points GPS client
            </label>
            <div className="flex gap-1 h-9">
              <SearchableSelect
                options={clientOptions}
                value={selectedClientId}
                onChange={setSelectedClientId}
                placeholder="Sélectionner un client..."
                searchPlaceholder="Rechercher un client..."
                className="flex-1"
              />
              {selectedClientId && (
                <button
                  onClick={() => setSelectedClientId('')}
                  className="h-9 w-9 flex items-center justify-center border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 ml-auto flex-shrink-0 h-9">
            <div className="flex items-center gap-3 text-xs text-gray-500 mr-2 h-full">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-600 inline-block" />GPS réelle</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-600 inline-block" />Estimée</span>
              {selectedClientId && <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block" />Client</span>}
            </div>
            <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 h-9">
              <MapPin className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 leading-none">Total</p>
                <p className="text-base font-bold text-gray-800 leading-tight">{stats.total}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 bg-green-50 rounded-lg px-3 h-9">
              <Navigation className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-xs text-gray-500 leading-none">GPS réelle</p>
                <p className="text-base font-bold text-green-700 leading-tight">{stats.gpsReal}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 bg-orange-50 rounded-lg px-3 h-9">
              <AlertCircle className="h-4 w-4 text-orange-400" />
              <div>
                <p className="text-xs text-gray-500 leading-none">Estimée</p>
                <p className="text-base font-bold text-orange-600 leading-tight">{stats.dropLocation}</p>
              </div>
            </div>
            {selectedClientId && (
              <div className="flex items-center gap-1.5 bg-blue-50 rounded-lg px-3 h-9">
                <MapPin className="h-4 w-4 text-blue-400" />
                <div>
                  <p className="text-xs text-gray-500 leading-none truncate max-w-[100px]">{selectedClient?.companyName || 'Client'}</p>
                  {clientLocationsLoading ? (
                    <RefreshCw className="h-3 w-3 text-blue-500 animate-spin mt-0.5" />
                  ) : (
                    <p className="text-base font-bold text-blue-700 leading-tight">{clientLocations.length} pts</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}
      </div>

      {/* Carte */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden" style={{ height: '600px' }}>
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />
          </div>
        ) : !selectedYear ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Sélectionnez une année pour afficher les positions</p>
            </div>
          </div>
        ) : positions.length === 0 && clientLocations.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucune position trouvée pour les filtres sélectionnés</p>
            </div>
          </div>
        ) : (
          <MapContainer center={mapCenter} zoom={8} className="h-full w-full">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {positions.map((pos) => (
              <Marker
                key={`task-${pos.taskId}`}
                position={[pos.lat, pos.lng]}
                icon={pos.positionSource === 'gps_real' ? gpsRealIcon : dropLocationIcon}
              >
                <Popup>
                  <div className="min-w-[240px]">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${pos.positionSource === 'gps_real' ? 'bg-green-600' : 'bg-orange-500'}`} />
                      <span className={`text-xs font-semibold ${pos.positionSource === 'gps_real' ? 'text-green-700' : 'text-orange-600'}`}>
                        {pos.positionSource === 'gps_real' ? 'Position GPS réelle' : 'Position estimée'}
                      </span>
                    </div>

                    <h3 className="font-bold text-gray-800 mb-2 text-sm leading-snug">{pos.clientName}</h3>

                    <div className="text-sm space-y-1 mb-3">
                      <p className="text-gray-600 flex items-center gap-1">
                        <User className="h-3 w-3 flex-shrink-0" />
                        <span>{pos.driverName}</span>
                      </p>
                      <p className="text-gray-600">
                        <strong>Fin de tâche:</strong>{' '}
                        {format(new Date(pos.endTime), 'dd MMM yyyy à HH:mm', { locale: fr })}
                      </p>
                      {pos.positionSource === 'gps_real' && pos.gpsDiffSeconds !== null && (
                        <p className="text-xs text-green-600">
                          GPS capturé à {pos.gpsDiffSeconds < 60
                            ? `${Math.round(pos.gpsDiffSeconds)}s`
                            : `${Math.round(pos.gpsDiffSeconds / 60)}min`} de la fin
                        </p>
                      )}
                      <p className="text-xs text-gray-400">
                        {pos.lat.toFixed(6)}, {pos.lng.toFixed(6)}
                      </p>
                    </div>

                    {savePoint.taskId === pos.taskId ? (
                      savePoint.success ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 flex items-center gap-2 text-green-700 text-xs">
                          <Check className="h-4 w-4 flex-shrink-0" />
                          Point enregistré avec succès !
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Nom du point</label>
                            <input
                              type="text"
                              value={savePoint.name}
                              onChange={(e) => setSavePoint(prev => ({ ...prev, name: e.target.value, error: null }))}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Nom du point GPS"
                              autoFocus
                            />
                          </div>
                          <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={savePoint.isPrimary}
                              onChange={(e) => setSavePoint(prev => ({ ...prev, isPrimary: e.target.checked }))}
                              className="rounded"
                            />
                            Point principal
                          </label>
                          {savePoint.error && <p className="text-xs text-red-600">{savePoint.error}</p>}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSavePoint(pos)}
                              disabled={savePoint.saving}
                              className="flex-1 px-2 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1"
                            >
                              {savePoint.saving ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                              Confirmer
                            </button>
                            <button
                              onClick={() => handleCancelSave(pos.taskId)}
                              disabled={savePoint.saving}
                              className="px-2 py-1.5 border border-gray-300 text-gray-600 rounded text-xs hover:bg-gray-50 disabled:opacity-50"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      )
                    ) : (
                      <button
                        onClick={() => handleOpenSavePoint(pos.taskId, pos)}
                        className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center justify-center gap-2"
                      >
                        <Save className="h-4 w-4" />
                        Enregistrer le point
                      </button>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            {clientLocations.map((loc) => (
              <Marker
                key={`cloc-${loc.id}`}
                position={[loc.position_lat, loc.position_lng]}
                icon={clientLocIcon}
              >
                <Popup>
                  <div className="min-w-[180px]">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-600 flex-shrink-0" />
                      <h3 className="font-bold text-gray-800 text-sm">{loc.name}</h3>
                    </div>
                    {loc.is_primary && (
                      <span className="inline-block text-xs bg-blue-100 text-blue-700 font-medium px-2 py-0.5 rounded-full mb-2">
                        Point principal
                      </span>
                    )}
                    {selectedClient && (
                      <p className="text-xs text-gray-600 mb-1">{selectedClient.companyName}</p>
                    )}
                    <div className="text-xs text-gray-400">
                      {loc.position_lat.toFixed(6)}, {loc.position_lng.toFixed(6)}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>
    </div>
  );
};

export default PositionsPage;
